import React, { useState } from 'react';
import debounce from 'lodash.debounce';
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@mweb/zappos-ui/Popover';

import { evProductBadgesEvent } from 'events/symphony';
import useMartyContext from 'hooks/useMartyContext';
import { track } from 'apis/amethyst';
import { evProductCalloutImpression } from 'events/product';
import type { TaxonomyAttribute } from 'types/cloudCatalog';
import ProductUtils, { BONUS_POINTS_ATTR, productCalloutIconMap } from 'helpers/ProductUtils';
import useEffectOnce from 'hooks/useEffectOnce';

import css from 'styles/components/productdetail/productCallout.scss';

interface ListElementProps {
  Icon?: React.ComponentType<{ hasCustomTooltip?: boolean }>;
  label: string;
  svgSrc?: string;
  tooltipText: string;
  badgeEventData?: any;
  testId?: string;
  amethystEnum?: string;
}

export const ProductListElement = ({ Icon, label, tooltipText, svgSrc, badgeEventData, testId, amethystEnum }: ListElementProps) => {
  const [isOpen, setIsOpen] = useState(false);
  useEffectOnce(() => {
    track(() => [evProductCalloutImpression, amethystEnum]);
  });

  const trackOpenCloseEvents = debounce(isOpen => {
    if (!badgeEventData) {
      return;
    }

    track(() => [badgeEventData.event, { opened: isOpen, closed: !isOpen, ...badgeEventData.data }]);
  }, 250);

  const openPopover = () => {
    setIsOpen(true);
    trackOpenCloseEvents(true);
  };

  const closePopover = () => {
    setIsOpen(false);
    trackOpenCloseEvents(false);
  };

  return (
    <li data-test-id={testId}>
      <Popover open={isOpen}>
        <PopoverTrigger onFocus={openPopover} onBlur={closePopover} onMouseEnter={openPopover} onMouseLeave={closePopover}>
          <div className="inline-flex flex-col items-center">
            {Icon && <Icon aria-hidden={true} hasCustomTooltip={true} />}
            {svgSrc && <img width={45} height={42} src={svgSrc} aria-hidden={true} alt="" />}
            <span>{label}</span>
          </div>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent
            className="border-subtle bg-default-minimal text-sm shadow-lg drop-shadow-lg"
            onOpenAutoFocus={(e: Event) => e.preventDefault()}
          >
            {tooltipText}
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    </li>
  );
};

interface ProductCalloutProps {
  testId?: string;
  brandId: string;
  rewardsBrandPromos: any; // TODO ts fix this once brandpromos ZCS is typed
  attributes: TaxonomyAttribute[];
  forKidsProductCallout: any; // TODO ts, not sure what the satus of this is, but could capture it in ZCS types
}

export const ProductCallout = (props: ProductCalloutProps) => {
  const {
    marketplace: { hasRewardsTransparency },
    testId
  } = useMartyContext();
  const { attributes: baseAttributes, brandId, forKidsProductCallout, rewardsBrandPromos = {}, testId: testIdProp = 'productBadge' } = props;
  const attributes: { value: string }[] = [...baseAttributes];

  /*
    checking for `rewardsBrandPromos`, and added an extra taxonomy attribute based on
    whether we're running a promo.
  */

  if (ProductUtils.hasRewards(hasRewardsTransparency, rewardsBrandPromos, brandId)) {
    attributes.push({ value: BONUS_POINTS_ATTR });
  }
  const labelMap = attributes.filter(attribute => productCalloutIconMap.get(attribute.value));

  const iconsWithLabels = labelMap.map(attribute => {
    const details = productCalloutIconMap.get(attribute.value);
    if (!details) {
      return null;
    }
    const { Icon, label, tooltipText, amethystEnum } = details;
    const badgeEventData = { event: evProductBadgesEvent, data: { badgeName: label } };

    return (
      <ProductListElement
        testId={testId(testIdProp)}
        key={label}
        Icon={Icon}
        label={label}
        tooltipText={tooltipText}
        badgeEventData={badgeEventData}
        amethystEnum={amethystEnum}
      />
    );
  });

  const { show: fkShow, brandId: fkBrandId, label: fkLabel, tooltipText: fkTooltipText, imgUrl: fkImgUrl } = forKidsProductCallout || {};
  if (fkShow === 'true' && brandId === fkBrandId) {
    iconsWithLabels.push(
      <ProductListElement testId={testId(testIdProp)} key={fkLabel} svgSrc={fkImgUrl} label={fkLabel} tooltipText={fkTooltipText} />
    );
  }

  // Renders the labels/callout only if there any to render (avoiding empty <ul></ul>)
  return iconsWithLabels?.length > 0 ? (
    <ul aria-label="Product Features" className={css.productCallout}>
      {' '}
      {iconsWithLabels}{' '}
    </ul>
  ) : null;
};

export default ProductCallout;
